import React from 'react';
import { Action } from './Button';

/*
 * Settings primitives.
 *
 * Every control in this system has to make its authority visible: a field the
 * organisation manages is shown, disabled, and labelled with who manages it,
 * rather than hidden. `managedBy` is how a row says that.
 */

const MARKER = { required: 'Required', optional: 'Optional' } as const;

/*
 * A visible label is only a label once it is tied to its control: a screen
 * reader otherwise names the input by its placeholder, which is gone the
 * moment anyone types. So when the field holds one form control that has no
 * id of its own, it gets one here and the label points at it, and the error
 * and hint are read as its description. A caller that passes htmlFor, or
 * gives the control its own id, keeps exactly what it set.
 */
type ControlProps = { id?: string; 'aria-describedby'?: string; 'aria-invalid'?: React.AriaAttributes['aria-invalid'] };

const isControl = (node: React.ReactNode): node is React.ReactElement<ControlProps> => {
  if (!React.isValidElement(node)) return false;
  return node.type === 'input' || node.type === 'select' || node.type === 'textarea' || node.type === Input || node.type === Select;
};

export const Field: React.FC<{
  label: string;
  hint?: React.ReactNode;
  managedBy?: string;
  htmlFor?: string;
  className?: string;
  /** Says beside the label whether the field must be filled in. */
  marker?: keyof typeof MARKER;
  /** What is wrong with the value, shown under the field. */
  error?: string;
  children: React.ReactNode;
}> = ({ label, hint, managedBy, htmlFor, className = '', marker, error, children }) => {
  const auto = React.useId();
  const only = React.Children.count(children) === 1 ? React.Children.toArray(children)[0] : null;
  let control = children;
  let target = htmlFor;
  let hintId: string | undefined;
  let errorId: string | undefined;
  if (!htmlFor && isControl(only)) {
    target = only.props.id || `field${auto}`;
    if (error) errorId = `${target}-error`;
    if (hint) hintId = `${target}-hint`;
    const describedBy = [only.props['aria-describedby'], errorId, hintId].filter(Boolean).join(' ') || undefined;
    let invalid = only.props['aria-invalid'];
    if (invalid === undefined && error) invalid = true;
    control = React.cloneElement(only, { id: target, 'aria-describedby': describedBy, 'aria-invalid': invalid });
  }
  return (
    <div className={className}>
      <label htmlFor={target} className="mb-1.5 block text-[13px] font-semibold text-ink">
        {label}
        {marker && <span className="ml-1.5 text-[11.5px] font-normal text-ink-soft">{MARKER[marker]}</span>}
      </label>
      {control}
      {error && <div id={errorId} role="alert" className="mt-1.5 text-[12px] leading-[20px] text-danger">{error}</div>}
      {managedBy && <div className="mt-1.5 text-[12px] text-ink-soft">{managedBy}</div>}
      {hint && <div id={hintId} className="mt-1.5 text-[12px] leading-[20px] text-ink-dim">{hint}</div>}
    </div>
  );
};

const CONTROL = 'w-full rounded-act border border-input bg-white px-3 py-2 text-[13px] text-ink outline-none transition-colors focus:border-ink focus:ring-[3px] focus:ring-ink/10 disabled:bg-canvas-inset disabled:text-ink-muted';

export const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ className = '', children, ...rest }) => (
  <select className={`${CONTROL} appearance-none bg-[length:16px] pr-9 ${className}`} {...rest}>{children}</select>
);

export const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ className = '', ...rest }) => (
  <input className={`${CONTROL} ${className}`} {...rest} />
);

export const FieldGrid: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <div className={`grid gap-x-5 gap-y-5 sm:grid-cols-2 ${className}`}>{children}</div>
);

export const Toggle: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}> = ({ checked, onChange, label, disabled = false }) => {
  let track = { true: 'bg-ink', false: 'bg-[#DEDEDE]' }[String(checked)];
  if (disabled) track = { true: 'bg-[#8A8B90] cursor-not-allowed', false: 'bg-[#E7E7E4] cursor-not-allowed' }[String(checked)];
  const knob = { true: 'translate-x-[22px]', false: 'translate-x-[3px]' }[String(checked)];
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-[26px] w-[46px] shrink-0 rounded-full transition-colors ${track}`}
    >
      <span className={`absolute left-0 top-[3px] h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${knob}`} />
    </button>
  );
};

export const SettingRow: React.FC<{
  title: React.ReactNode;
  desc?: React.ReactNode;
  control: React.ReactNode;
  className?: string;
}> = ({ title, desc, control, className = '' }) => (
  <div className={`flex items-center justify-between gap-6 px-6 py-4 ${className}`}>
    <div className="min-w-0">
      <div className="text-[13px] font-semibold text-ink">{title}</div>
      {desc && <div className="mt-0.5 text-[13px] leading-[21.5px] text-ink-dim">{desc}</div>}
    </div>
    <div className="shrink-0">{control}</div>
  </div>
);

/*
 * The save bar. Revert is drawn even when nothing has changed, disabled with a
 * reason, so the pair never appears and disappears under the pointer.
 */
export const SaveBar: React.FC<{
  onSave: () => void;
  onRevert: () => void;
  saveLabel: string;
  dirty: boolean;
  busy?: boolean;
}> = ({ onSave, onRevert, saveLabel, dirty, busy = false }) => (
  <div className="mt-6 flex items-center justify-end gap-2 border-t border-line pt-5">
    <Action variant="outline" onClick={onRevert} disabled={!dirty} reason="Nothing has changed yet.">
      Revert
    </Action>
    <Action onClick={onSave} disabled={!dirty || busy} reason="Nothing has changed yet.">
      {saveLabel}
    </Action>
  </div>
);
